"use client"

import { useEffect, useRef } from "react"
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import type { Property } from "@/types/api"

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN || "YOUR_MAPBOX_ACCESS_TOKEN_HERE"

interface PropertyMapProps {
  properties?: Property[]
}

interface PropertyCluster {
  coordinate: [number, number]
  properties: Property[]
}

/**
 * Mapbox map with markers for all properties and a default marker in the center of Spain.
 * Used for show/hide on the propiedades page.
 */
export function PropertyMap({ properties = [] }: PropertyMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const markers = useRef<mapboxgl.Marker[]>([])

  // Function to clear all existing markers
  const clearMarkers = () => {
    markers.current.forEach(marker => marker.remove())
    markers.current = []
  }

  // Function to group properties by identical coordinates
  const clusterProperties = (properties: Property[]): PropertyCluster[] => {
    const clusters = new Map<string, {coordinate: [number, number], properties: Property[]}>()
    
    properties.forEach(property => {
      if (property.coordinates && property.coordinates[0] !== 0 && property.coordinates[1] !== 0) {
        // Use a more precise key with fixed decimal places to handle floating point precision
        const lng = Number(property.coordinates[0].toFixed(6))
        const lat = Number(property.coordinates[1].toFixed(6))
        const key = `${lng},${lat}`
        
        if (!clusters.has(key)) {
          clusters.set(key, {
            coordinate: [lng, lat] as [number, number],
            properties: []
          })
        }
        clusters.get(key)!.properties.push(property)
      }
    })

    return Array.from(clusters.values())
  }

  // Function to create offset coordinates for multiple properties at same location
  const getOffsetCoordinates = (baseCoord: [number, number], index: number, total: number): [number, number] => {
    if (total === 1) return baseCoord
    
    // Validate base coordinates
    if (!baseCoord || baseCoord.length !== 2 || isNaN(baseCoord[0]) || isNaN(baseCoord[1])) {
      console.warn('Invalid base coordinates for offset:', baseCoord)
      return [0, 0]
    }
    
    // Create a small circular offset pattern
    const radius = 0.0002 // Very small offset in degrees (~20 meters)
    const angle = (2 * Math.PI * index) / total
    
    const offsetLng = baseCoord[0] + radius * Math.cos(angle)
    const offsetLat = baseCoord[1] + radius * Math.sin(angle)
    
    // Ensure the result is valid
    if (isNaN(offsetLng) || isNaN(offsetLat)) {
      console.warn('Generated invalid offset coordinates:', [offsetLng, offsetLat])
      return baseCoord
    }
    
    return [offsetLng, offsetLat]
  }

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return

    mapboxgl.accessToken = MAPBOX_TOKEN

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [-3.7038, 40.4168], // Centro de España (Madrid)
      zoom: 5.5,
      minZoom: 4,
      maxZoom: 18,
    })

    // Controles de navegación (zoom in/out)
    map.current.addControl(new mapboxgl.NavigationControl(), "top-right")

    // Add custom styles for popup close button
    const style = document.createElement('style')
    style.textContent = `
      .mapboxgl-popup-close-button {
        font-size: 1.5rem !important;
        width: 20px !important;
        height: 20px !important;
        right: 5px !important;
        top: 5px !important;
      }
    `
    document.head.appendChild(style)
  }, [])

  // Update markers when properties change
  useEffect(() => {
    if (!map.current) return

    // Clear existing markers
    clearMarkers()

    // Add markers for all properties
    if (properties.length > 0) {
      const clusters = clusterProperties(properties)
      
      clusters.forEach(cluster => {
        const { coordinate, properties: clusterProps } = cluster
        
        // Debug: Validate coordinates before creating markers
        if (!coordinate || coordinate.length !== 2 || isNaN(coordinate[0]) || isNaN(coordinate[1])) {
          console.warn('Invalid coordinates for cluster:', coordinate, clusterProps.map(p => p.id))
          return
        }
        
        if (clusterProps.length === 1) {
          // Single property - normal marker
          const property = clusterProps[0]
          // Validate property coordinates
          if (!property.coordinates || property.coordinates.length !== 2 || isNaN(property.coordinates[0]) || isNaN(property.coordinates[1]) || property.coordinates[0] === 0 || property.coordinates[1] === 0) {
            console.warn('Skipping property with invalid coordinates:', property.id, property.coordinates)
            return
          }
          
          // Create wrapper div for the marker to handle hover effects
          const markerWrapper = document.createElement("div")
          markerWrapper.style.cssText = `
            width: 25px;
            height: 25px;
            position: relative;
          `
          
          const markerElement = document.createElement("div")
          markerElement.style.cssText = `
            background-color: #A87C3F;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            cursor: pointer;
            transition: width 0.2s ease, height 0.2s ease, margin 0.2s ease;
            position: absolute;
            top: 0;
            left: 0;
          `
          
          // Use width/height changes instead of transform for hover effect
          markerWrapper.addEventListener('mouseenter', () => {
            markerElement.style.width = '30px'
            markerElement.style.height = '30px'
            markerElement.style.margin = '-2.5px'
          })
          markerWrapper.addEventListener('mouseleave', () => {
            markerElement.style.width = '100%'
            markerElement.style.height = '100%'
            markerElement.style.margin = '0'
          })
          
          markerWrapper.appendChild(markerElement)
          
          const marker = new mapboxgl.Marker(markerWrapper)
            .setLngLat(coordinate)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(`
                <div style="padding: 12px; min-width: 200px;">
                  <h3 style="margin: 0 0 8px 0; font-weight: bold; color: #003366; font-size: 16px;">${property.name}</h3>
                  <p style="margin: 0 0 4px 0; color: #A87C3F; font-weight: bold; font-size: 18px;">€${property.price.toLocaleString('es-ES')}/mes</p>
                  <p style="margin: 0 0 4px 0; color: #666; font-size: 14px;">${property.address}</p>
                  <p style="margin: 0; color: #888; font-size: 12px;">${property.bathrooms} baños • ${property.roommates} compañeros</p>
                  <a href="/propiedades/${property.id}" style="display: inline-block; margin-top: 8px; padding: 4px 12px; background-color: #A87C3F; color: white; text-decoration: none; border-radius: 4px; font-size: 12px;">Ver detalles</a>
                </div>
              `)
            )
            .addTo(map.current!)
          markers.current.push(marker)
        } else {
          // Multiple properties at same location
          if (clusterProps.length <= 4) {
            // Small cluster - create offset markers
            clusterProps.forEach((property, index) => {
              // Validate property coordinates
              if (!property.coordinates || property.coordinates.length !== 2 || isNaN(property.coordinates[0]) || isNaN(property.coordinates[1]) || property.coordinates[0] === 0 || property.coordinates[1] === 0) {
                console.warn('Skipping property in cluster with invalid coordinates:', property.id, property.coordinates)
                return
              }
              const offsetCoord = getOffsetCoordinates(coordinate, index, clusterProps.length)
              // Validate offset coordinates
              if (!offsetCoord || offsetCoord.length !== 2 || isNaN(offsetCoord[0]) || isNaN(offsetCoord[1]) || offsetCoord[0] === 0 || offsetCoord[1] === 0) {
                console.warn('Skipping marker with invalid offset coordinates:', offsetCoord, property.id)
                return
              }
              
              // Create wrapper div for the marker
              const markerWrapper = document.createElement("div")
              markerWrapper.style.cssText = `
                width: 22px;
                height: 22px;
                position: relative;
              `
              
              const markerElement = document.createElement("div")
              markerElement.style.cssText = `
                background-color: #A87C3F;
                width: 100%;
                height: 100%;
                border-radius: 50%;
                border: 2px solid white;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                cursor: pointer;
                transition: width 0.2s ease, height 0.2s ease, margin 0.2s ease;
                position: absolute;
                top: 0;
                left: 0;
              `
              
              markerWrapper.addEventListener('mouseenter', () => {
                markerElement.style.width = '28px'
                markerElement.style.height = '28px'
                markerElement.style.margin = '-3px'
              })
              markerWrapper.addEventListener('mouseleave', () => {
                markerElement.style.width = '100%'
                markerElement.style.height = '100%'
                markerElement.style.margin = '0'
              })
              
              markerWrapper.appendChild(markerElement)
              
              const marker = new mapboxgl.Marker(markerWrapper)
                .setLngLat(offsetCoord)
                .setPopup(
                  new mapboxgl.Popup({ offset: 25 }).setHTML(`
                    <div style="padding: 12px; min-width: 200px;">
                      <h3 style="margin: 0 0 8px 0; font-weight: bold; color: #003366; font-size: 16px;">${property.name}</h3>
                      <p style="margin: 0 0 4px 0; color: #A87C3F; font-weight: bold; font-size: 18px;">€${property.price.toLocaleString('es-ES')}/mes</p>
                      <p style="margin: 0 0 4px 0; color: #666; font-size: 14px;">${property.address}</p>
                      <p style="margin: 0; color: #888; font-size: 12px;">${property.bathrooms} baños • ${property.roommates} compañeros</p>
                      <a href="/propiedades/${property.id}" style="display: inline-block; margin-top: 8px; padding: 4px 12px; background-color: #A87C3F; color: white; text-decoration: none; border-radius: 4px; font-size: 12px;">Ver detalles</a>
                    </div>
                  `)
                )
                .addTo(map.current!)
              markers.current.push(marker)
            })
          } else {
            // Large cluster - create cluster marker
            // Validate cluster coordinates
            if (!coordinate || coordinate.length !== 2 || isNaN(coordinate[0]) || isNaN(coordinate[1]) || coordinate[0] === 0 || coordinate[1] === 0) {
              console.warn('Skipping cluster marker with invalid coordinates:', coordinate, clusterProps.map(p => p.id))
              return
            }
            
            // Create wrapper div for the cluster marker
            const markerWrapper = document.createElement("div")
            markerWrapper.style.cssText = `
              width: 35px;
              height: 35px;
              position: relative;
            `
            
            const markerElement = document.createElement("div")
            markerElement.style.cssText = `
              background-color: #A87C3F;
              width: 100%;
              height: 100%;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 6px rgba(0,0,0,0.4);
              cursor: pointer;
              transition: width 0.2s ease, height 0.2s ease, margin 0.2s ease;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 12px;
              position: absolute;
              top: 0;
              left: 0;
            `
            markerElement.textContent = clusterProps.length.toString()
            
            markerWrapper.addEventListener('mouseenter', () => {
              markerElement.style.width = '42px'
              markerElement.style.height = '42px'
              markerElement.style.margin = '-3.5px'
            })
            markerWrapper.addEventListener('mouseleave', () => {
              markerElement.style.width = '100%'
              markerElement.style.height = '100%'
              markerElement.style.margin = '0'
            })
            
            markerWrapper.appendChild(markerElement)
            
            // Create detailed popup for cluster
            const clusterPopupContent = `
              <div style="padding: 12px; min-width: 250px; max-height: 400px; overflow-y: auto;">
                <h3 style="margin: 0 0 12px 0; font-weight: bold; color: #003366; font-size: 16px; text-align: center;">
                  ${clusterProps.length} Alojamientos en esta ubicación
                </h3>
                ${clusterProps.map(property => `
                  <div style="border-bottom: 1px solid #eee; padding: 8px 0; margin-bottom: 8px;">
                    <h4 style="margin: 0 0 4px 0; font-weight: bold; color: #003366; font-size: 14px;">${property.name}</h4>
                    <p style="margin: 0 0 4px 0; color: #A87C3F; font-weight: bold; font-size: 16px;">€${property.price.toLocaleString('es-ES')}/mes</p>
                    <p style="margin: 0 0 4px 0; color: #888; font-size: 12px;">${property.bathrooms} baños • ${property.roommates} compañeros</p>
                    <a href="/propiedades/${property.id}" style="display: inline-block; margin-top: 4px; padding: 3px 8px; background-color: #A87C3F; color: white; text-decoration: none; border-radius: 3px; font-size: 11px;">Ver detalles</a>
                  </div>
                `).join('')}
              </div>
            `
            const marker = new mapboxgl.Marker(markerWrapper)
              .setLngLat(coordinate)
              .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(clusterPopupContent))
              .addTo(map.current!)
            markers.current.push(marker)
          }
        }
      })
      
      // If we have properties, fit the map to show all markers
      const allCoordinates = clusters.map(cluster => cluster.coordinate)
      
      if (allCoordinates.length > 0) {
        const bounds = new mapboxgl.LngLatBounds()
        allCoordinates.forEach(coord => bounds.extend(coord))
        map.current.fitBounds(bounds, { padding: 50, maxZoom: 10 })
      }
    } else {
      // Add default marker in the center of Spain when no properties
      const markerWrapper = document.createElement("div")
      markerWrapper.style.cssText = `
        width: 30px;
        height: 30px;
        position: relative;
      `
      
      const markerElement = document.createElement("div")
      markerElement.style.cssText = `
        background-color: #A87C3F;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        cursor: pointer;
        position: absolute;
        top: 0;
        left: 0;
      `
      
      markerWrapper.appendChild(markerElement)

      const marker = new mapboxgl.Marker(markerWrapper)
        .setLngLat([-3.7038, 40.4168])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 }).setHTML(`
            <div style="padding: 12px; text-align: center;">
              <h3 style="margin: 0 0 8px 0; font-weight: bold; color: #003366;">España</h3>
              <p style="margin: 0; color: #666; font-size: 14px;">Centro de España</p>
            </div>
          `)
        )
        .addTo(map.current!)
      markers.current.push(marker)
    }
  }, [properties])

  // Limpieza al desmontar
  useEffect(() => {
    return () => {
      clearMarkers()
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [])

  return (
    <div className="w-full h-full rounded-lg overflow-hidden shadow-lg relative">
      <div ref={mapContainer} className="w-full h-full" />

      {MAPBOX_TOKEN === "YOUR_MAPBOX_ACCESS_TOKEN_HERE" && (
        <div className="absolute inset-0 bg-gray-200 flex items-center justify-center rounded-lg">
          <div className="text-center p-6">
            <p className="text-gray-600 mb-2">Mapa no disponible</p>
            <p className="text-sm text-gray-500">Configura tu token de Mapbox</p>
          </div>
        </div>
      )}
    </div>
  )
}